import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** Desk density — plan/36. Not a theme; form Advanced stays in both. */
export type DeskDensity = "everyday" | "power";

const STORAGE_KEY = "icraftx.deskDensity";

type DensityContextValue = {
  density: DeskDensity;
  setDensity: (next: DeskDensity) => void;
  toggleDensity: () => void;
};

const DensityContext = createContext<DensityContextValue | null>(null);

function readStored(): DeskDensity {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "power" || raw === "everyday") return raw;
  } catch {
    /* private mode */
  }
  return "everyday";
}

export function DensityProvider(props: { children: ReactNode }) {
  const [density, setDensityState] = useState<DeskDensity>(() =>
    typeof window === "undefined" ? "everyday" : readStored(),
  );

  const setDensity = useCallback((next: DeskDensity) => {
    setDensityState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleDensity = useCallback(() => {
    setDensity(density === "everyday" ? "power" : "everyday");
  }, [density, setDensity]);

  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  const value = useMemo(
    () => ({ density, setDensity, toggleDensity }),
    [density, setDensity, toggleDensity],
  );

  return (
    <DensityContext.Provider value={value}>
      {props.children}
    </DensityContext.Provider>
  );
}

export function useDeskDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    throw new Error("useDeskDensity requires DensityProvider");
  }
  return ctx;
}
